<script setup>
/**
 * KaptionChatListHeader
 *
 * Drop-in replacement for ChatListHeader when KAPTION_PLATFORM_CHAT_LIST
 * feature flag is enabled. Combines:
 * - Title area with context-aware actions (folder edit/delete, filter save/clear)
 * - KaptionPlatformActionTopbar (cycling filters, categories)
 * - Layout toggle
 *
 * NOTICE: KAPTION-INTEGRATION
 */
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { formatNumber } from '@chatwoot/utils';
import KaptionPlatformActionTopbar from './KaptionPlatformActionTopbar.vue';
import SwitchLayout from 'dashboard/routes/dashboard/conversation/search/SwitchLayout.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import { useUISettings } from 'dashboard/composables/useUISettings';
import wootConstants from 'dashboard/constants/globals';

const props = defineProps({
  // From ChatListHeader
  pageTitle: {
    type: String,
    default: '',
  },
  hasAppliedFilters: {
    type: Boolean,
    default: false,
  },
  hasActiveFolders: {
    type: Boolean,
    default: false,
  },
  isOnExpandedLayout: {
    type: Boolean,
    default: false,
  },
  conversationStats: {
    type: Object,
    default: () => ({}),
  },
  isListLoading: {
    type: Boolean,
    default: false,
  },

  // Kaption-specific
  isDarkMode: {
    type: Boolean,
    default: true,
  },
  favorites: {
    type: Array,
    default: () => [],
  },
  folders: {
    type: Array,
    default: () => [],
  },
  labels: {
    type: Array,
    default: () => [],
  },
  channels: {
    type: Array,
    default: () => [],
  },
  teams: {
    type: Array,
    default: () => [],
  },
  assistants: {
    type: Array,
    default: () => [],
  },
  currentAssigneeFilter: {
    type: String,
    default: 'all',
    validator: v => ['all', 'me', 'unassigned', 'unread'].includes(v),
  },
  // SID: mark-all-read.UI.1 — show the bulk Mark-all-read action in the header
  canMarkAllRead: {
    type: Boolean,
    default: false,
  },
  currentStatusFilter: {
    type: String,
    default: 'open',
    validator: v => ['open', 'resolved', 'snoozed', 'all'].includes(v),
  },

  // Selection state (for title display)
  selectedFavorite: {
    type: Object,
    default: null,
  },
  selectedFolder: {
    type: Object,
    default: null,
  },
  selectedLabel: {
    type: Object,
    default: null,
  },
  selectedChannel: {
    type: Object,
    default: null,
  },
  selectedTeam: {
    type: Object,
    default: null,
  },
  selectedAssistant: {
    type: Object,
    default: null,
  },
  // Whether we're on the "All Conversations" view (dashboard route with no category selected)
  isAllConversationsView: {
    type: Boolean,
    default: false,
  },
  // Current conversation type (mention, unattended, etc.)
  conversationType: {
    type: String,
    default: '',
  },
  // Current sort order for conversations
  currentSort: {
    type: String,
    default: wootConstants.SORT_BY_TYPE.LAST_ACTIVITY_AT_DESC,
  },
  // NOTICE: KAPTION-INTEGRATION - Single-pane conversation search.
  showConversationSearch: {
    type: Boolean,
    default: false,
  },
  conversationSearchQuery: {
    type: String,
    default: '',
  },
});

const emit = defineEmits([
  // From ChatListHeader
  'addFolders',
  'deleteFolders',
  'resetFilters',
  'filtersModal',

  // Kaption-specific
  'filterChange',
  'statusFilterChange',
  'sortChange',
  'favoriteClick',
  'folderClick',
  'labelClick',
  'channelClick',
  'teamClick',
  'assistantClick',
  'toggleFavorite',
  'navigateToMentions',
  'navigateToUnattended',
  'navigateToAllConversations',
  'markAllRead',
  'conversationSearchQueryChange',
]);

const { t } = useI18n();

// Layout toggle (handled internally like original ChatListHeader)
const { uiSettings, updateUISettings } = useUISettings();

// Computed: display title based on selection and context
const displayTitle = computed(() => {
  // Selection-based titles take priority
  if (props.selectedFavorite) return props.selectedFavorite.name;
  if (props.selectedFolder) return props.selectedFolder.name;
  if (props.selectedLabel) return `#${props.selectedLabel.name}`;
  if (props.selectedChannel) return props.selectedChannel.name;
  if (props.selectedTeam) return props.selectedTeam.name;
  if (props.selectedAssistant) return props.selectedAssistant.name;

  // Special conversation type titles (mentions, unattended)
  if (props.conversationType === 'mention')
    return t('KAPTION_PLATFORM.CHAT_LIST.MENTIONS');
  if (props.conversationType === 'unattended')
    return t('KAPTION_PLATFORM.CHAT_LIST.UNATTENDED');

  // Context-based titles
  if (props.hasActiveFolders) return props.pageTitle; // folder name from ChatList
  if (props.hasAppliedFilters)
    return t('KAPTION_PLATFORM.CHAT_LIST.FILTERED_RESULTS');

  // Default based on assignee filter
  const titleKeys = {
    all: 'KAPTION_PLATFORM.CHAT_LIST.ALL_CONVERSATIONS',
    me: 'KAPTION_PLATFORM.CHAT_LIST.MY_CONVERSATIONS',
    unassigned: 'KAPTION_PLATFORM.CHAT_LIST.UNASSIGNED',
    // NOTICE: KAPTION-INTEGRATION - Unread filter title
    unread: 'KAPTION_PLATFORM.CHAT_LIST.UNREAD',
  };
  return t(
    titleKeys[props.currentAssigneeFilter] ||
      'KAPTION_PLATFORM.CHAT_LIST.CONVERSATIONS'
  );
});

// NOTICE: KAPTION-INTEGRATION
// Computed: display subtitle for selected channel (inbox)
// Shows phone number for WhatsApp/Twilio, instagram handle for Instagram, etc.
const displaySubtitle = computed(() => {
  if (!props.selectedChannel) return null;

  const channel = props.selectedChannel;
  const channelType = channel.channel_type;

  // WhatsApp: show phone number
  if (channelType === 'Channel::Whatsapp' && channel.phone_number) {
    return channel.phone_number;
  }

  // Instagram: show instagram_id prefixed with @
  if (channelType === 'Channel::Instagram' && channel.instagram_id) {
    return `@${channel.instagram_id}`;
  }

  // Twilio SMS: show phone number
  if (channelType === 'Channel::TwilioSms' && channel.phone_number) {
    return channel.phone_number;
  }

  // Email: show email address
  if (channelType === 'Channel::Email' && channel.email) {
    return channel.email;
  }

  // Telegram: show bot name prefixed with @
  if (channelType === 'Channel::Telegram' && channel.bot_name) {
    return `@${channel.bot_name}`;
  }

  return null;
});

// Computed: conversation count
const allCount = computed(() => props.conversationStats?.allCount || 0);
const formattedAllCount = computed(() => formatNumber(allCount.value));

// Computed: show count badge
const showCountBadge = computed(() => {
  return (
    allCount.value > 0 &&
    (props.hasAppliedFilters || props.hasActiveFolders) &&
    !props.isListLoading
  );
});

// Computed: disable cycling filter when folders/filters are active
const disableCyclingFilter = computed(() => {
  return props.hasActiveFolders || props.hasAppliedFilters;
});

// Map assignee filter values for topbar
// ChatList uses 'me', topbar uses 'mine'
// NOTICE: KAPTION-INTEGRATION - 'unread' passes through unchanged.
const topbarAssigneeFilter = computed(() => {
  // When on mentions/unattended route, show that as the current filter
  if (props.conversationType === 'mention') return 'mentions';
  if (props.conversationType === 'unattended') return 'unattended';
  // Map assignee filter values for topbar - ChatList uses 'me', topbar uses 'mine'
  const mapping = {
    all: 'all',
    me: 'mine',
    unassigned: 'unassigned',
    unread: 'unread',
  };
  return mapping[props.currentAssigneeFilter] || 'all';
});

// Event handlers - bubble up to parent
function handleFilterChange(filter) {
  emit('filterChange', filter);
}

function handleStatusFilterChange(status) {
  emit('statusFilterChange', status);
}

function handleSortChange(sortValue) {
  emit('sortChange', sortValue);
}

function handleFavoriteClick(favorite) {
  emit('favoriteClick', favorite);
}

function handleFolderClick(folder) {
  emit('folderClick', folder);
}

function handleLabelClick(label) {
  emit('labelClick', label);
}

function handleChannelClick(channel) {
  emit('channelClick', channel);
}

function handleTeamClick(team) {
  emit('teamClick', team);
}

function handleAssistantClick(assistant) {
  emit('assistantClick', assistant);
}

function handleToggleFavorite(payload) {
  emit('toggleFavorite', payload);
}

// Navigation handlers for extended filter cycle
function handleNavigateToMentions() {
  emit('navigateToMentions');
}

function handleNavigateToUnattended() {
  emit('navigateToUnattended');
}

function handleNavigateToAllConversations(filter) {
  emit('navigateToAllConversations', filter);
}

// Layout toggle - handled internally like original ChatListHeader
function handleToggleLayout() {
  const { LAYOUT_TYPES } = wootConstants;
  const {
    conversation_display_type: conversationDisplayType = LAYOUT_TYPES.CONDENSED,
  } = uiSettings.value;
  const newViewType =
    conversationDisplayType === LAYOUT_TYPES.CONDENSED
      ? LAYOUT_TYPES.EXPANDED
      : LAYOUT_TYPES.CONDENSED;
  updateUISettings({
    conversation_display_type: newViewType,
    previously_used_conversation_display_type: newViewType,
  });
}

function handleConversationSearchInput(event) {
  emit('conversationSearchQueryChange', event.target.value);
}

function clearConversationSearch() {
  emit('conversationSearchQueryChange', '');
}

// Text/background color classes based on dark mode
// Use transparent background to inherit from parent (ChatList uses bg-n-solid-1)
const containerClass = computed(() => 'bg-transparent');

const titleTextClass = computed(() =>
  props.isDarkMode ? 'text-white' : 'text-gray-900'
);

// NOTICE: KAPTION-INTEGRATION - Subtitle styling for inbox identifiers
const subtitleTextClass = computed(() =>
  props.isDarkMode ? 'text-white/60' : 'text-gray-500'
);

const countBadgeClass = computed(() =>
  props.isDarkMode ? 'bg-white/10 text-white/70' : 'bg-gray-100 text-gray-600'
);

const conversationSearchPlaceholder = computed(() =>
  t('KAPTION_PLATFORM.CONVERSATION_SEARCH.PLACEHOLDER')
);
const clearConversationSearchLabel = computed(() =>
  t('KAPTION_PLATFORM.CONVERSATION_SEARCH.CLEAR')
);
</script>

<template>
  <div :class="containerClass">
    <!-- Title Area -->
    <div
      class="flex items-center justify-between gap-2 px-3 py-2"
      :class="{
        'border-b border-n-weak': hasAppliedFilters || hasActiveFolders,
      }"
    >
      <!-- Title + Subtitle + Count -->
      <div class="flex items-center justify-center min-w-0 gap-2">
        <div class="flex flex-col min-w-0">
          <h1
            class="text-sm font-semibold truncate"
            :class="titleTextClass"
            :title="displayTitle"
          >
            {{ displayTitle }}
          </h1>
          <!-- NOTICE: KAPTION-INTEGRATION - Subtitle showing inbox identifier -->
          <span
            v-if="displaySubtitle"
            class="text-xs truncate leading-tight"
            :class="subtitleTextClass"
            :title="displaySubtitle"
          >
            {{ displaySubtitle }}
          </span>
        </div>
        <!-- NOTICE: KAPTION-INTEGRATION - rounded-full for pill-shaped appearance -->
        <span
          v-if="showCountBadge"
          class="px-2 py-0.5 text-xs shrink-0 rounded-full"
          :class="countBadgeClass"
          :title="String(allCount)"
        >
          {{ formattedAllCount }}
        </span>
      </div>

      <!-- Action Buttons (right side) -->
      <div class="flex items-center gap-1">
        <!-- Folder actions when folder is active -->
        <template v-if="hasActiveFolders">
          <div class="relative">
            <NextButton
              id="kaptionEditFolderButton"
              v-tooltip.top-end="$t('FILTER.CUSTOM_VIEWS.EDIT.EDIT_BUTTON')"
              icon="i-lucide-pen-line"
              slate
              xs
              faded
              @click="emit('filtersModal')"
            />
            <!-- Teleport target for ConversationFilter modal -->
            <div
              id="conversationFilterTeleportTarget"
              class="absolute z-50 mt-2"
              :class="{ 'ltr:right-0 rtl:left-0': isOnExpandedLayout }"
            />
            <div
              id="kaptionFolderEditTeleportTarget"
              class="absolute z-50 mt-2"
              :class="{ 'ltr:right-0 rtl:left-0': isOnExpandedLayout }"
            />
          </div>
          <NextButton
            id="kaptionDeleteFolderButton"
            v-tooltip.top-end="$t('FILTER.CUSTOM_VIEWS.DELETE.DELETE_BUTTON')"
            icon="i-lucide-trash-2"
            ruby
            xs
            faded
            @click="emit('deleteFolders')"
          />
        </template>

        <!-- Filter actions when filters are applied (but not a folder) -->
        <template v-else-if="hasAppliedFilters">
          <div class="relative">
            <NextButton
              v-tooltip.top-end="$t('FILTER.CUSTOM_VIEWS.ADD.SAVE_BUTTON')"
              icon="i-lucide-save"
              slate
              xs
              faded
              @click="emit('addFolders')"
            />
            <div
              id="kaptionSaveFilterTeleportTarget"
              class="absolute z-50 mt-2"
              :class="{ 'ltr:right-0 rtl:left-0': isOnExpandedLayout }"
            />
          </div>
          <NextButton
            v-tooltip.top-end="$t('FILTER.CLEAR_BUTTON_LABEL')"
            icon="i-lucide-circle-x"
            ruby
            faded
            xs
            @click="emit('resetFilters')"
          />
        </template>

        <!-- NOTICE: KAPTION-INTEGRATION - Bulk Mark-all-read (Unread tab only) -->
        <!-- SID: mark-all-read.UI.1 -->
        <NextButton
          v-if="canMarkAllRead"
          v-tooltip.top-end="$t('CHAT_LIST.MARK_ALL_READ.BUTTON')"
          icon="i-lucide-check-check"
          slate
          xs
          faded
          @click="emit('markAllRead')"
        />

        <!-- Layout toggle (always visible) -->
        <SwitchLayout
          :is-on-expanded-layout="isOnExpandedLayout"
          @toggle="handleToggleLayout"
        />
      </div>
    </div>

    <!-- NOTICE: KAPTION-INTEGRATION - WhatsApp Web-style search stays in the chat-list pane. -->
    <div v-if="showConversationSearch" class="px-3 pb-2">
      <div
        class="flex items-center w-full gap-2 px-2 py-1 text-sm leading-5 rounded-2xl h-7 outline outline-1 outline-n-weak bg-n-solid-3 dark:bg-n-black/30 text-n-slate-12 focus-within:outline-n-brand/50 focus-within:bg-n-solid-2"
      >
        <span class="i-lucide-search size-4 text-n-slate-9 shrink-0" />
        <input
          :value="conversationSearchQuery"
          type="text"
          role="searchbox"
          class="kaption-conversation-search-input w-full min-w-0 h-5 text-sm bg-transparent text-n-slate-12"
          :placeholder="conversationSearchPlaceholder"
          autocomplete="off"
          @input="handleConversationSearchInput"
        />
        <button
          v-if="conversationSearchQuery"
          type="button"
          class="text-n-slate-9 hover:text-n-slate-12"
          :aria-label="clearConversationSearchLabel"
          @click="clearConversationSearch"
        >
          <span class="i-lucide-x size-4" />
        </button>
      </div>
    </div>

    <!-- Kaption Platform Action Topbar -->
    <KaptionPlatformActionTopbar
      :is-dark-mode="isDarkMode"
      :favorites="favorites"
      :folders="folders"
      :labels="labels"
      :channels="channels"
      :teams="teams"
      :assistants="assistants"
      :initial-filter="topbarAssigneeFilter"
      :initial-status-filter="currentStatusFilter"
      :current-sort="currentSort"
      :disable-cycling-filter="disableCyclingFilter"
      show-settings-panel
      :selected-team="selectedTeam"
      :selected-channel="selectedChannel"
      :selected-label="selectedLabel"
      :selected-folder="selectedFolder"
      :selected-assistant="selectedAssistant"
      :is-all-conversations-view="isAllConversationsView"
      :conversation-type="conversationType"
      @filter-change="handleFilterChange"
      @status-filter-change="handleStatusFilterChange"
      @sort-change="handleSortChange"
      @favorite-click="handleFavoriteClick"
      @folder-click="handleFolderClick"
      @label-click="handleLabelClick"
      @channel-click="handleChannelClick"
      @team-click="handleTeamClick"
      @assistant-click="handleAssistantClick"
      @toggle-favorite="handleToggleFavorite"
      @navigate-to-mentions="handleNavigateToMentions"
      @navigate-to-unattended="handleNavigateToUnattended"
      @navigate-to-all-conversations="handleNavigateToAllConversations"
    />
  </div>
</template>

<style scoped>
.kaption-conversation-search-input {
  height: 20px !important;
  min-height: 20px !important;
  margin: 0 !important;
  padding: 0 !important;
  border: 0 !important;
  border-radius: 0 !important;
  outline: 0 !important;
  box-shadow: none !important;
  background: transparent !important;
  font-size: 14px !important;
  line-height: 20px !important;
  appearance: none !important;
  -webkit-appearance: none !important;
}

.kaption-conversation-search-input:focus,
.kaption-conversation-search-input:focus-visible {
  border: 0 !important;
  outline: 0 !important;
  box-shadow: none !important;
}

.kaption-conversation-search-input::placeholder {
  color: currentColor !important;
  opacity: 0.45 !important;
}
</style>
